import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { RichText } from '@7f-ui/components/rich-text/rich-text';
import type { NexperienceFragment } from '@data-access/lib/gql/experience/documents';
import { useExperienceCancelationDeadline } from '@feat/feat-experience/booking-detail';
import type { IconProps } from '@phosphor-icons/react';
import { Baby, CalendarBlank, ChatText, ClipboardText, ClockClockwise, HourglassSimple, Info, ThumbsUp, UsersFour } from '@phosphor-icons/react';
import { NON_CANCELABLE } from '@utils/utils-config';
import type { ComponentType, PropsWithChildren } from 'react';
import { sanitizeBackendHtml } from '@/lib/security/sanitize-html';
import { useExperienceLanguages } from './use-app-experience-languages';
import { useExperienceOfferPeriod } from './use-app-experience-offer-period';
import { useExperienceRegistrationDeadline } from './use-app-experience-registration-deadline';
export interface AppExperienceImportantInformationProps {
  experience: NexperienceFragment;
  duration: string;
}
export function AppExperienceImportantInformation(t0) {
  const $ = _c(33);
  const {
    experience,
    duration
  } = t0;
  const registrationDeadline = useExperienceRegistrationDeadline(experience);
  const cancelationDeadline = useExperienceCancelationDeadline(experience.cancelationDeadline);
  const languages = useExperienceLanguages(experience.languages);
  const offerPeriod = useExperienceOfferPeriod(experience);
  let t1;
  if ($[0] !== experience.requirements) {
    t1 = experience.requirements ?? [];
    $[0] = experience.requirements;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const requirements = t1;
  let t2;
  if ($[2] !== experience.importantNotesHtml) {
    t2 = experience.importantNotesHtml ?? [];
    $[2] = experience.importantNotesHtml;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const importantNotes = t2;
  let t3;
  if ($[4] !== offerPeriod) {
    t3 = offerPeriod && <OverviewItem Icon={CalendarBlank} title={"Verf\xFCgbare Termine"}>{offerPeriod}</OverviewItem>;
    $[4] = offerPeriod;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== duration) {
    t4 = duration && <OverviewItem Icon={ClockClockwise} title="Dauer">{duration}</OverviewItem>;
    $[6] = duration;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== experience.minAge) {
    t5 = Boolean(experience.minAge) && <OverviewItem Icon={Baby} title="Mindestalter">{`Geeignet für Personen ab ${experience.minAge?.toString() ?? ""} Jahren`}</OverviewItem>;
    $[8] = experience.minAge;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== registrationDeadline) {
    t6 = <OverviewItem Icon={HourglassSimple} title="Anmeldeschluss">{registrationDeadline}</OverviewItem>;
    $[10] = registrationDeadline;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = experience.cancelationDeadline < NON_CANCELABLE ? `Kostenlos bis ${cancelationDeadline ?? ""} vor Beginn` : "Keine Stornierung m\xF6glich";
  let t8;
  if ($[12] !== t7) {
    t8 = <OverviewItem Icon={ThumbsUp} title="Stornierung">{t7}</OverviewItem>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== requirements) {
    t9 = requirements.length > 0 && <OverviewItem Icon={ClipboardText} title={"Vorraussetzungen f\xFCr dieses Erlebnis"}>{requirements.map(_temp)}</OverviewItem>;
    $[14] = requirements;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== experience.maxParticipants) {
    t10 = experience.maxParticipants && <OverviewItem Icon={UsersFour} title="Anzahl Teilnehmende">{experience.maxParticipants}</OverviewItem>;
    $[16] = experience.maxParticipants;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== experience.languages || $[19] !== languages) {
    t11 = experience.languages && <OverviewItem Icon={ChatText} title="Sprachen">{languages}</OverviewItem>;
    $[18] = experience.languages;
    $[19] = languages;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== importantNotes) {
    t12 = importantNotes.length > 0 && <div className="flex flex-col gap-y-8 [column-span:all] sm:gap-y-10"><OverviewItem Icon={Info} title="Weitere Infos"><ul className={cx({
          "list-disc pl-4": importantNotes.length > 1
        })}>{importantNotes.map(_temp2)}</ul></OverviewItem></div>;
    $[21] = importantNotes;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t8 || $[31] !== t9) {
    t13 = <div className="flex flex-col gap-10">{t3}{t4}{t5}{t6}{t8}{t9}{t10}{t11}{t12}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t8;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  return t13;
}
function _temp2(__html, index_0) {
  return <li key={index_0}><RichText text={sanitizeBackendHtml(__html)} /></li>;
}
function _temp(requirement, index) {
  return <div key={index} className="font-body-copy" dangerouslySetInnerHTML={{
    __html: sanitizeBackendHtml(requirement)
  }} />;
}
function OverviewItem(t0) {
  const $ = _c(12);
  const {
    title,
    Icon,
    children
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <Icon className="size-6 shrink-0" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== title) {
    t2 = <h3 className="font-heading5">{title}</h3>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <div className="font-body-copy">{children}</div>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="flex gap-4">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}